import {
  AutoComplete,
  ISelectType,
  useFormContext,
} from "@4uhub/lib4uhub";
import { Grid } from "@mui/material";
import { memo, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useWatch } from "react-hook-form";
import { listMainGenerics } from "../../../../../../../services/generic.service";
import { iServiceListByAutomation } from "../../../../../../../services/iService.service";
import { TIntegrationRuleIServiceForm } from "./integrationRuleIServiceSchema";
import { IIntegrationRuleIServiceFieldsProps } from "./models";

const translationPath = "page.4uBus.integrationRule.integrationRuleIService.";
const SYNC_BUS_AUTOMATION_CODES = [14, 15, 16, 17];
const MULTIDIRECTIONAL_INTEGRATION_DIRECTION_TYPE_CODE = "4";

const isSyncAutomation = (busAutomationCode: number | null) =>
  busAutomationCode !== null &&
  SYNC_BUS_AUTOMATION_CODES.includes(busAutomationCode);

const listIntegrationDirectionTypesByAutomation = async (params?: {
  busAutomationCode: number | null;
}) => {
  const response = await listMainGenerics({
    Identifier: "IntegrationDirectionType",
  });

  const syncAutomation = isSyncAutomation(params?.busAutomationCode ?? null);

  return {
    ...response,
    data: response.data.filter((directionType: ISelectType) =>
      syncAutomation
        ? directionType.code === MULTIDIRECTIONAL_INTEGRATION_DIRECTION_TYPE_CODE
        : directionType.code !==
          MULTIDIRECTIONAL_INTEGRATION_DIRECTION_TYPE_CODE,
    ),
  };
};

export const IntegrationRuleIServiceFields = memo(
  ({
    busAutomationCode,
    busAutomationId,
  }: IIntegrationRuleIServiceFieldsProps) => {
    const { t } = useTranslation();
    const { control, setValue } =
      useFormContext<TIntegrationRuleIServiceForm>();

    const integrationDirectionType = useWatch({
      control,
      name: "integrationDirectionType",
    }) as ISelectType | null;

    const integrationDirectionTypeParams = useMemo(
      () => ({
        busAutomationCode,
      }),
      [busAutomationCode],
    );

    const iServiceParams = useMemo(
      () => ({
        automationId: busAutomationId,
      }),
      [busAutomationId],
    );

    useEffect(() => {
      if (!integrationDirectionType) return;

      const syncAutomation = isSyncAutomation(busAutomationCode);
      const isMultidirectional =
        integrationDirectionType.code ===
        MULTIDIRECTIONAL_INTEGRATION_DIRECTION_TYPE_CODE;

      if (
        (syncAutomation && !isMultidirectional) ||
        (!syncAutomation && isMultidirectional)
      ) {
        setValue("integrationDirectionType", null as any);
      }
    }, [busAutomationCode, integrationDirectionType, setValue]);

    return (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <AutoComplete
            request={listIntegrationDirectionTypesByAutomation}
            params={integrationDirectionTypeParams}
            name="integrationDirectionType"
            size="small"
            disable={busAutomationCode === null}
            getOptionLabel={({ name }) => name}
            label={t(translationPath + "integration_direction_type")}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <AutoComplete
            request={iServiceListByAutomation}
            params={iServiceParams}
            name="iService"
            size="small"
            disable={!busAutomationId}
            getOptionLabel={({ name }) => name}
            label={t(translationPath + "i_service")}
          />
        </Grid>
      </Grid>
    );
  },
);
